<template>
  <div class="">
    <h4 v-if="!editingPaymentconfig">
      {{ form.hash ? `${$t('ct-edit-product')}: ` + form.name : $t('ct-create-product') }}
    </h4>
    <h4 v-if="editingPaymentconfig">{{ $t('ct-checkout-gateway-config') }}</h4>
    <div v-if="formStep === 0">
      <!-- Input Name -->
      <div class="form-group">
        <label for="name" class="col-sm-2 control-label">{{ $t('ct-name') }}</label>
        <div class="col-sm-10">
          <input type="text" class="form-control" id="name" name="name" @input="e.remove('name')" v-model="form.name" :class="{'is-invalid': e.has('name')}">
          <small class="form-text text-danger" v-if="e.has('name')">{{ e.first('name') }}</small>
        </div>
      </div>
      <!-- Input Description ---TEXT--- -->
      <div class="form-group">
        <label for="description" class="col-sm-2 control-label">{{ $t('ct-description') }}</label>
        <div class="col-sm-10">
          <textarea class="form-control" @input="e.remove('description')" id="description" name="description" v-model="form.description" :class="{'is-invalid': e.has('description')}"></textarea>
          <small class="form-text text-danger" v-if="e.has('description')">{{ e.first('description') }}</small>
        </div>
      </div>
      <!-- Select Language  -->
      <div class="form-group pl-3 col-sm-10">
        <label for="language">{{ $t('ct-language') }}</label>
        <select id="language" class="form-control" @change="e.remove('language')" v-model="form.language" :class="{'is-invalid': e.has('language')}">
          <option :value="null">{{ $t('ct-checkout-select-language') }}</option>
          <option v-for="language in options.languages" :value="language.language" :key="language.language">{{ language.name }}</option>
        </select>
        <small class="form-text text-danger" v-if="e.has('language')">{{ e.first('language') }}</small>
      </div>
      <!-- Options Format -->
      <div class="form-group pl-3 col-sm-10">
        <span class="d-block mb-2">{{ $t('ct-format') }}</span>
        <div class="form-check form-check-inline" v-for="format in options.formats" :key="format.id">
          <input class="form-check-input" type="radio" name="format" @input="e.remove('format')" :id="`format-${format.id}`" v-model="form.format" :value="format.id">
          <label class="form-check-label" :for="`format-${format.id}`">
            {{ $t('ct-checkout-'+format.name) }}
          </label>
        </div>
      </div>
      <!-- Always Ask Address -->
      <div class="form-group pl-3 col-sm-10">
        <span class="d-block mb-2">{{ $t('ct-checkout-ask-address') }}</span>
        <div class="form-check">
          <input class="form-check-input" type="checkbox" name="askAddress" v-model="form.askAddress" id="askAddress">
          <label class="form-check-label" for="askAddress">
            {{ $t('ct-checkout-always-ask-address') }}
          </label>
        </div>
      </div>
      <!-- Select Niche -->
      <div class="form-group pl-3 col-sm-10">
        <label for="Niche" class="control-label">{{ $t('ct-niche') }}</label>
        <select id="Niche" class="form-control" @change="e.remove('niche')" v-model="form.niche" :class="{'is-invalid': e.has('niche')}">
          <option :value="null">{{ $t('ct-checkout-chose-niche') }}</option>
          <option v-for="niche in options.niches" :key="niche.id" :value="niche.id">{{ $t('ct-checkout-'+niche.name) }}</option>
        </select>
        <small class="form-text text-danger" v-if="e.has('niche')">{{ e.first('niche') }}</small>
      </div>
    </div>
    <div v-if="formStep === 1">
      <pricing-payment-options
        v-model="form.paymentOptions"
        :is-recurrent="form.type === 2"
        :currency="form.currency"
        :info="options.user"
        :auth="auth"
        @editing="editingPaymentconfig = $event"
      />
      <div v-if="!editingPaymentconfig">
        <!-- group Price -->
        <div class="form-group pl-3 col-sm-10">
          <span class="d-block control-label mb-2">{{ $t('ct-price') }}</span>
          <div class="input-group">
            <div class="input-group-prepend">
              <select class="form-control" v-model="form.currency">
                <option :value="null">{{ $t('ct-currency') }}</option>
                <option v-for="(currency, code) in options.currencies" :key="code" :value="code">{{ `${code}: ${currency.name}` }}</option>
              </select>
            </div>
            <input type="text" class="form-control"
               v-money="money"
               v-model="form.price"
               @input="e.remove('price')"
               :class="{'is-invalid': e.has('price')}"
            >
          </div>
          <span class="form-text text-danger text-right" v-if="e.has('price')">{{ e.first('price') }}</span>
        </div>
        <!-- Select refund period -->
        <div class="form-group pl-3 col-sm-10">
          <label for="refund">{{ $t('ct-checkout-refund-period') }}</label>
          <select id="refund" class="form-control" v-model="form.refundPeriod">
            <option :value="null">{{ $t('ct-checkout-no-refund') }}</option>
            <option v-for="period in ['7', '14', '30']" :key="period" :value="period">{{ period }}</option>
          </select>
        </div>
      </div>
    </div>
    <div v-if="formStep === 2">
      <!-- Select ThankYouPage -->
      <div class="form-group pl-3 col-sm-10">
        <label for="ty-page">{{ $t('ct-checkout-ty-page') }}</label>
        <select id="ty-page" class="form-control" @change="e.remove('thankYouPage')" v-model="form.thankYouPage" :class="{'is-invalid': e.has('thankYouPage')}">
          <option :value="null">{{ $t('ct-checkout-select-page') }}</option>
          <option v-for="page in options.pages" :key="page.id" :value="`${siteUrl}/${page.id}`">{{ page.name }}</option>
        </select>
        <small class="form-text text-danger" v-if="e.has('thankYouPage')">{{ e.first('thankYouPage') }}</small>
      </div>
      <!-- Select Sales Page -->
      <div class="form-group pl-3 col-sm-10">
        <label for="sales-page">{{ $t('ct-checkout-sales-page') }}</label>
        <select id="sales-page" class="form-control" @change="e.remove('salesPage')" v-model="form.salesPage" :class="{'is-invalid': e.has('salesPage')}">
          <option :value="null">{{ $t('ct-checkout-select-page') }}</option>
          <option v-for="page in options.pages" :key="page.id" :value="`${siteUrl}/${page.id}`">{{ page.name }}</option>
        </select>
        <small class="form-text text-danger" v-if="e.has('salesPage')">{{ e.first('salesPage') }}</small>
      </div>
    </div>
    <div class="pl-3" v-if="!editingPaymentconfig">
      <button class="btn btn-default" @click="$emit('done')" v-if="[0,2].includes(formStep)">{{ $t('ct-cancel') }}</button>
      <button class="btn btn-primary" @click="formStep--" v-if="formStep > 0">{{ $t('ct-previous') }}</button>
      <button class="btn btn-primary" @click="next" v-if="formStep < 2">{{ $t('ct-next') }}</button>
      <button class="btn btn-success" @click="next" v-if="formStep === 2">{{ $t('ct-save') }}</button>
    </div>

  </div>
</template>

<script>
import {
  getProduct,
  getProducts,
  getSiteInfo,
  saveProduct
} from '../../../../../js/services/integrations-internas/supercheckout';
import {getBit} from '../../../../../js/services/editor/page';
import Errors from '../Util/errors';
import PricingPaymentOptions from './PricingPaymentOptions';

export default {
  name: 'CheckoutProductForm',
  props: ['auth', 'config'],
  components: {PricingPaymentOptions},
  data() {
    let form = {
      hash: null,
      name: '',
      description: '',
      format: 1,
      askAddress: false,
      niche: null,
      image: '',
      price: '1.00',
      currency: 'USD',
      language: null,
      thankYouPage: null,
      salesPage: null,
      refundPeriod: null,
      paymentOptions: [],
      type: 1
    };
    return {
      siteUrl: 'https://sitebuilder-editor.omb11.com/#/super-checkout-redirect',
      formStep: 0,
      form,
      originalForm: JSON.stringify(form),
      loading: {
        products: false
      },
      editingPaymentconfig: false,
      options: {
        pages: []
      },
      e: new Errors([]),
      money: {
        decimal: '.',
        thousands: '',
        prefix: '',
        suffix: '',
        precision: 2,
        masked: false
      }
    };
  },

  methods: {
    next() {
      this.validate(this.formStep);
      if (this.e.any()) {
        return;
      }

      if (this.formStep === 2) {
        return this.saveProduct();
      }

      this.formStep++;
    },

    editProduct(product) {
      this.loading.products = true;
      getProduct(this.auth.token, product.hash).then(({data}) => {
        const sellable = {
          hash: data.product.hash,
          versionId: data.product.versions[0].id,
          type: data.product.versions[0].payment_type,
          name: data.product.name,
          description: data.product.versions[0].description,
          format: data.product.format_id,
          askAddress: data.product.versions[0].always_address,
          niche: data.product.niche_id,
          price: (data.product.versions[0].price / 100).toFixed(2),
          paymentOptions: data.product.versions[0].payment_options,
          currency: data.product.versions[0].currency,
          language: data.product.versions[0].language,
          thankYouPage: data.product.versions[0].thank_you_page_url,
          salesPage: data.product.versions[0].sales_page_url,
          refundPeriod: data.product.versions[0].refund_period,
          template_id: this.config.steps === 3 ? 6 : this.config.steps === 2 ? 5 : 7
        };
        this.form = {
          ...this.form,
          ...sellable
        };

        this.loading.products = false;
      });
    },

    trans(key, replace = null) {
      window.trans(key, replace);
    },

    resetForm() {
      this.formStep = 0;
      this.form = JSON.parse(this.originalForm);
      this.e.clear();
      this.e.clearValidations();
    },

    saveProduct() {
      saveProduct(this.auth.token, this.form).then(async({data}) => {
        this.resetForm();
        this.$snotify.success(this.$t('ct-success-true'));
        await getProducts(this.auth.token).then(({data}) => {
          this.$emit('done', data.products);
          this.loading.products = false;
          this.resetForm();
        });
      });
    },

    validate(formStep) {
      const validations = [
        {
          type: 'required|numeric|min:1|max:3',
          name: 'required|min:4',
          description: 'required|min:10',
          language: 'required',
          format: 'required',
          askAddress: 'bool',
          niche: 'required'
        },
        {
          price: 'required|numeric',
          currency: 'required',
          refundPeriod: ''
        },
        {
          thankYouPage: 'required',
          salesPage: 'required'
        }
      ];
      this.e.clear();
      this.e.clearValidations();
      this.e.validation(validations[formStep]);

      return this.e.validate(this.form);
    }
  },

  async created() {
    await getSiteInfo(this.auth.token).then(({data}) => {
      this.options = data;
    });
    await getBit(this.$route.params.page_id).then(({data}) => {
      this.options.pages = data.layout.pages;
      let url = this.options.pages.find(p => p.id === data.id).id;
      this.form.salesPage = `${this.siteUrl}/${url}`;
    });
    if (!this.form.hash) {
      this.resetForm();
    }
  },

  beforeDestroy() {
    this.resetForm();
  }
};
</script>



// WEBPACK FOOTER //
// src/components/editor/apps/Checkout/Form/CheckoutProductForm.vue